import { SweetspotError } from '@/helpers/error'

import {
  AgentName,
  BuiltRunMessages,
  ChatCompactionStatus,
  ChatMessage,
  ChatRunState,
  RunIdToMsgIndex,
  V3LiveCursor,
} from '@/types/chat-v2'
import { UUID } from '@/types/Id'

const v3SyncFetchTails = new Map<string, Promise<void>>()

const V3_SYNC_REQUEST_TIMEOUT_MS = 15_000
const V3_SYNC_MAX_ATTEMPTS = 3
const V3_SYNC_RETRY_BACKOFF_MS = 500

/**
 * POSTs a v3 sync request.
 *
 * Each attempt is time-bounded, because syncs for a session are serialized
 * and one hung request would block every later one. Timeouts, network errors
 * and 5xx responses are retried a few times: the post-run sync is what settles
 * a finished run, so a single transient failure there would fail the run.
 */
export async function postV3Sync(
  url: string,
  request: unknown,
  accessToken: string,
): Promise<Response> {
  for (let attempt = 1; ; attempt += 1) {
    const controller = new AbortController()
    const timeout = setTimeout(
      () => controller.abort(),
      V3_SYNC_REQUEST_TIMEOUT_MS,
    )
    let res: Response | null = null
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(request),
        signal: controller.signal,
      })
    } catch (error) {
      if (attempt >= V3_SYNC_MAX_ATTEMPTS) throw error
    } finally {
      clearTimeout(timeout)
    }

    if (res?.ok) return res
    if (res && (res.status < 500 || attempt >= V3_SYNC_MAX_ATTEMPTS)) {
      throw new SweetspotError({
        name: 'FETCH_ERROR',
        message: res.statusText,
        cause: {
          status: res.status,
        },
      })
    }
    await new Promise((resolve) =>
      setTimeout(resolve, V3_SYNC_RETRY_BACKOFF_MS * attempt),
    )
  }
}

/**
 * Serializes v3 sync fetches per (agent, session).
 *
 * A sync fetch reads the shared sync cache, awaits the server, then merges
 * and writes the cache back. Two concurrent fetches interleave those steps:
 * a request that read the SERVER before a finalization write can resolve
 * AFTER a request that read it later, regressing the cache — and the session
 * projection built from it — to the older manifest. Digests only fire on
 * manifest change, so nothing re-syncs after such a regression until an
 * unrelated fetch or a manual refresh (observed as cancelled runs missing
 * their terminal state and tool results "sometimes").
 *
 * Serialization makes cache-write order equal server-read order; server
 * state is monotonic, so the cache can no longer regress.
 *
 * The tail key is built HERE from the typed agent name and session id, so
 * callers can neither typo a scope prefix nor collide across agents.
 */
export function serializeV3SyncFetch<T>(
  agentName: AgentName,
  // '' is the disabled-query sentinel the sync query-key args carry.
  sessionId: UUID | '',
  fetchSync: () => Promise<T>,
): Promise<T> {
  const key = `${agentName}:${sessionId}`
  const previous = v3SyncFetchTails.get(key) ?? Promise.resolve()
  const run = previous.then(fetchSync, fetchSync)
  const tail = run.then(
    () => undefined,
    () => undefined,
  )
  v3SyncFetchTails.set(key, tail)
  void tail.then(() => {
    if (v3SyncFetchTails.get(key) === tail) v3SyncFetchTails.delete(key)
  })
  return run
}

export interface V3ObjectWithId {
  object_id: string
}

export interface V3SyncDigestLike {
  manifest_id: string
  active_run_id: UUID | null
  overlay_object_id: string | null
  stream_state: 'idle' | 'streaming' | 'finishing'
}

export interface V3SyncRunRefLike {
  run_id: UUID
  parent_run_id: UUID | null
  object_id: string
  start_index: number
  message_count: number
  created_at: string
}

export interface V3SyncRunObjectLike extends V3ObjectWithId {
  run_id: UUID
  initialized_by_user_id: UUID | null
  overridden_model_name: string | null
  messages: ChatMessage['message'][]
  deferred_tool_calls?: ChatMessage['deferred_tool_calls']
}

export interface V3SyncOverlayMessageLike {
  message_id: RunIdToMsgIndex
  parent_id: RunIdToMsgIndex | null
  run_id: UUID
  run_parent_id: UUID | null
  run_user_id: UUID | null
  index_in_thread: number
  message: ChatMessage['message']
  deferred_tool_calls?: ChatMessage['deferred_tool_calls']
  last_sequence?: number | null
  body_offsets?: Record<string, number>
}

export interface V3SyncOverlayObjectLike<
  OverlayMessageT extends V3SyncOverlayMessageLike,
> extends V3ObjectWithId {
  run_id: UUID
  parent_run_id: UUID | null
  messages: OverlayMessageT[]
  compaction_status?: ChatCompactionStatus
}

export interface V3SyncManifestLike<
  RunRefT extends V3SyncRunRefLike,
> extends V3ObjectWithId {
  meta_object_id: string
  view_object_id: string
  active_run_id: UUID | null
  run_refs: RunRefT[]
  overlay_object_id: string | null
}

export interface V3SyncCacheLike<
  DigestT extends V3SyncDigestLike,
  ManifestT extends V3SyncManifestLike<RunRefT>,
  MetaObjectT extends V3ObjectWithId,
  ViewObjectT extends V3ObjectWithId,
  RunRefT extends V3SyncRunRefLike,
  RunObjectT extends V3SyncRunObjectLike,
  OverlayObjectT extends V3SyncOverlayObjectLike<OverlayMessageT>,
  OverlayMessageT extends V3SyncOverlayMessageLike,
  SubagentSnapshotObjectT extends V3ObjectWithId = V3ObjectWithId,
> {
  digest: DigestT | null
  manifest: ManifestT | null
  meta_objects: Record<string, MetaObjectT>
  view_objects: Record<string, ViewObjectT>
  run_objects: Record<string, RunObjectT>
  overlay_objects: Record<string, OverlayObjectT>
  subagent_snapshot_objects: Record<string, SubagentSnapshotObjectT>
  built_run_messages: BuiltRunMessages
}

export interface V3SyncResponseLike<
  DigestT extends V3SyncDigestLike,
  ManifestT extends V3SyncManifestLike<RunRefT>,
  MetaObjectT extends V3ObjectWithId,
  ViewObjectT extends V3ObjectWithId,
  RunRefT extends V3SyncRunRefLike,
  RunObjectT extends V3SyncRunObjectLike,
  OverlayObjectT extends V3SyncOverlayObjectLike<OverlayMessageT>,
  OverlayMessageT extends V3SyncOverlayMessageLike,
  SubagentSnapshotObjectT extends V3ObjectWithId = V3ObjectWithId,
> {
  digest: DigestT
  manifest: ManifestT
  meta_object?: MetaObjectT | null
  view_object?: ViewObjectT | null
  run_objects: RunObjectT[]
  overlay_object?: OverlayObjectT | null
  subagent_snapshot_objects?: SubagentSnapshotObjectT[]
}

export interface V3SyncDecision {
  shouldSync: boolean
  reason: string
}

export function createEmptyV3SyncCache<
  DigestT extends V3SyncDigestLike,
  ManifestT extends V3SyncManifestLike<RunRefT>,
  MetaObjectT extends V3ObjectWithId,
  ViewObjectT extends V3ObjectWithId,
  RunRefT extends V3SyncRunRefLike,
  RunObjectT extends V3SyncRunObjectLike,
  OverlayObjectT extends V3SyncOverlayObjectLike<OverlayMessageT>,
  OverlayMessageT extends V3SyncOverlayMessageLike,
  SubagentSnapshotObjectT extends V3ObjectWithId = V3ObjectWithId,
>(): V3SyncCacheLike<
  DigestT,
  ManifestT,
  MetaObjectT,
  ViewObjectT,
  RunRefT,
  RunObjectT,
  OverlayObjectT,
  OverlayMessageT,
  SubagentSnapshotObjectT
> {
  return {
    digest: null,
    manifest: null,
    meta_objects: {},
    view_objects: {},
    run_objects: {},
    overlay_objects: {},
    subagent_snapshot_objects: {},
    built_run_messages: new WeakMap(),
  }
}

export function collectKnownV3SyncObjectIds(
  cache: Pick<
    V3SyncCacheLike<any, any, any, any, any, any, any, any>,
    | 'meta_objects'
    | 'view_objects'
    | 'run_objects'
    | 'overlay_objects'
    | 'subagent_snapshot_objects'
  >,
): string[] {
  return [
    ...Object.keys(cache.meta_objects),
    ...Object.keys(cache.view_objects),
    ...Object.keys(cache.run_objects),
    ...Object.keys(cache.overlay_objects),
    ...Object.keys(cache.subagent_snapshot_objects),
  ]
}

type V3MessagePart = ChatMessage['message']['parts'][number]

function partBody(part: V3MessagePart): string | null {
  if (part.part_kind === 'text' || part.part_kind === 'thinking') {
    return part.content
  }
  if (part.part_kind === 'tool-call' && typeof part.args === 'string') {
    return part.args
  }
  return null
}

const utf8 = new TextEncoder()

type V3ResumeOverlay = Pick<
  V3SyncOverlayObjectLike<V3SyncOverlayMessageLike>,
  'run_id' | 'messages'
>

function currentV3Overlay(
  cache: Pick<
    V3SyncCacheLike<any, any, any, any, any, any, any, any>,
    'manifest' | 'overlay_objects'
  >,
): V3ResumeOverlay | undefined {
  const id = cache.manifest?.overlay_object_id
  return id ? cache.overlay_objects[id] : undefined
}

/**
 * The live run as the client already holds it, for a sync to resume from: the
 * messages the client is streaming when it has them, else the cached overlay.
 */
export function previousV3Overlay(
  cache: Pick<
    V3SyncCacheLike<any, any, any, any, any, any, any, any>,
    'manifest' | 'overlay_objects'
  >,
  streamed: readonly ChatMessage[] | undefined,
): V3ResumeOverlay | undefined {
  const last = streamed?.at(-1)
  if (!streamed || !last) return currentV3Overlay(cache)
  return {
    run_id: last.run_id,
    messages: streamed.map((message) => ({
      message_id: message.id,
      parent_id: message.parent_id,
      run_id: message.run_id,
      run_parent_id: message.run_parent_id,
      run_user_id: message.run_user_id,
      index_in_thread: message.index_in_thread,
      message: message.message,
      deferred_tool_calls: message.deferred_tool_calls,
      last_sequence: message.last_delta_sequence,
    })),
  }
}

/**
 * What `previous` already holds of each streamed message, so a sync sends only
 * newer messages and the text appended since.
 */
export function collectV3LiveCursor(
  previous: V3ResumeOverlay | undefined,
): V3LiveCursor | null {
  const cursor: V3LiveCursor = {}
  for (const message of previous?.messages ?? []) {
    if (message.last_sequence == null) continue
    const lengths: Record<string, number> = {}
    message.message.parts.forEach((part: V3MessagePart, index: number) => {
      const body = partBody(part)
      if (body !== null) lengths[index] = utf8.encode(body).length
    })
    cursor[message.message_id] = { seq: message.last_sequence, lengths }
  }
  return Object.keys(cursor).length ? cursor : null
}

/**
 * A resumed overlay in full: messages the cursor held unchanged come from the
 * cached overlay, and resumed bodies are appended to the cached text.
 */
function resumeV3Overlay<
  OverlayMessageT extends V3SyncOverlayMessageLike,
  OverlayObjectT extends V3SyncOverlayObjectLike<OverlayMessageT>,
>(
  previous: V3ResumeOverlay | undefined,
  received: OverlayObjectT,
): OverlayObjectT {
  if (previous?.run_id !== received.run_id) return received
  const prior = new Map(previous.messages.map((m) => [m.message_id, m]))
  const receivedIds = new Set(received.messages.map((m) => m.message_id))
  const held = previous.messages.filter(
    (m) =>
      m.run_id === received.run_id &&
      m.last_sequence != null &&
      !receivedIds.has(m.message_id),
  ) as OverlayMessageT[]
  const resumed = received.messages.map((message) => {
    const offsets = message.body_offsets
    const before = prior.get(message.message_id)
    if (!offsets || !before || !Object.keys(offsets).length) return message
    const parts = message.message.parts.map((part, index) => {
      const prefix = offsets[index]
        ? partBody(before.message.parts[index])
        : null
      if (prefix === null) return part
      const body = prefix + (partBody(part) ?? '')
      return part.part_kind === 'tool-call'
        ? { ...part, args: body }
        : { ...part, content: body }
    })
    return {
      ...message,
      message: { ...message.message, parts },
      body_offsets: undefined,
    } as OverlayMessageT
  })
  return {
    ...received,
    messages: [...held, ...resumed].sort(
      (a, b) => a.index_in_thread - b.index_in_thread,
    ),
  }
}

export function decideWhetherToSyncV3Digest(
  cache:
    | Pick<
        V3SyncCacheLike<any, any, any, any, any, any, any, any>,
        'digest' | 'manifest'
      >
    | undefined,
  digest: V3SyncDigestLike,
): V3SyncDecision {
  const cachedDigest = cache?.digest ?? null

  if (cachedDigest === null || cache?.manifest == null) {
    return {
      shouldSync: true,
      reason: 'no_cached_manifest',
    }
  }

  if (digest.stream_state !== 'idle') {
    return {
      shouldSync: true,
      reason:
        cachedDigest.manifest_id === digest.manifest_id
          ? 'active_overlay_reconnect'
          : 'active_overlay_manifest_changed',
    }
  }

  if (cachedDigest.manifest_id !== digest.manifest_id) {
    return {
      shouldSync: true,
      reason: 'manifest_id_changed',
    }
  }

  if (cachedDigest.overlay_object_id !== digest.overlay_object_id) {
    return {
      shouldSync: true,
      reason: 'overlay_object_changed',
    }
  }

  if (cachedDigest.active_run_id !== digest.active_run_id) {
    return {
      shouldSync: true,
      reason: 'active_run_changed',
    }
  }

  if (cachedDigest.stream_state !== digest.stream_state) {
    return {
      shouldSync: true,
      reason: 'stream_state_changed',
    }
  }

  return {
    shouldSync: false,
    reason: 'digest_matched',
  }
}

export function mergeV3SyncCache<
  DigestT extends V3SyncDigestLike,
  ManifestT extends V3SyncManifestLike<RunRefT>,
  MetaObjectT extends V3ObjectWithId,
  ViewObjectT extends V3ObjectWithId,
  RunRefT extends V3SyncRunRefLike,
  RunObjectT extends V3SyncRunObjectLike,
  OverlayObjectT extends V3SyncOverlayObjectLike<OverlayMessageT>,
  OverlayMessageT extends V3SyncOverlayMessageLike,
  SubagentSnapshotObjectT extends V3ObjectWithId = V3ObjectWithId,
>(
  cache: V3SyncCacheLike<
    DigestT,
    ManifestT,
    MetaObjectT,
    ViewObjectT,
    RunRefT,
    RunObjectT,
    OverlayObjectT,
    OverlayMessageT,
    SubagentSnapshotObjectT
  >,
  response: V3SyncResponseLike<
    DigestT,
    ManifestT,
    MetaObjectT,
    ViewObjectT,
    RunRefT,
    RunObjectT,
    OverlayObjectT,
    OverlayMessageT,
    SubagentSnapshotObjectT
  >,
  previous: V3ResumeOverlay | undefined = currentV3Overlay(cache),
): V3SyncCacheLike<
  DigestT,
  ManifestT,
  MetaObjectT,
  ViewObjectT,
  RunRefT,
  RunObjectT,
  OverlayObjectT,
  OverlayMessageT,
  SubagentSnapshotObjectT
> {
  return {
    digest: response.digest,
    manifest: response.manifest,
    meta_objects:
      response.meta_object == null
        ? cache.meta_objects
        : {
            ...cache.meta_objects,
            [response.meta_object.object_id]: response.meta_object,
          },
    view_objects:
      response.view_object == null
        ? cache.view_objects
        : {
            ...cache.view_objects,
            [response.view_object.object_id]: response.view_object,
          },
    run_objects: response.run_objects.reduce<Record<string, RunObjectT>>(
      (acc, runObject) => {
        acc[runObject.object_id] = runObject
        return acc
      },
      { ...cache.run_objects },
    ),
    // Only the overlay the manifest names: older ones are never read again.
    overlay_objects: (() => {
      const id = response.manifest.overlay_object_id
      const overlay = response.overlay_object
        ? resumeV3Overlay(previous, response.overlay_object)
        : id
          ? cache.overlay_objects[id]
          : undefined
      return overlay ? { [overlay.object_id]: overlay } : {}
    })(),
    subagent_snapshot_objects: (
      response.subagent_snapshot_objects ?? []
    ).reduce<Record<string, SubagentSnapshotObjectT>>(
      (acc, snapshotObject) => {
        acc[snapshotObject.object_id] = snapshotObject
        return acc
      },
      { ...cache.subagent_snapshot_objects },
    ),
    built_run_messages: cache.built_run_messages,
  }
}

export function toChatRunStateFromV3StreamState(
  streamState: V3SyncDigestLike['stream_state'],
): ChatRunState {
  switch (streamState) {
    case 'idle':
      return 'idle'
    case 'streaming':
      return 'streaming'
    case 'finishing':
      return 'finishing'
  }
}

export function toActiveRunIdFromV3Digest(
  digest: Pick<V3SyncDigestLike, 'stream_state' | 'active_run_id'>,
): UUID | null {
  return digest.stream_state === 'idle' ? null : digest.active_run_id
}
export function getCompactionStatusFromV3Overlay(
  overlayObjects: Readonly<
    Record<string, { compaction_status?: ChatCompactionStatus }>
  >,
  overlayObjectId: string | null,
): ChatCompactionStatus {
  if (overlayObjectId === null) return 'idle'
  return overlayObjects[overlayObjectId]?.compaction_status ?? 'idle'
}

export function getCompactionStatusFromV3SyncCache(
  cache:
    | {
        digest: Pick<V3SyncDigestLike, 'overlay_object_id'> | null
        overlay_objects: Readonly<
          Record<string, { compaction_status?: ChatCompactionStatus }>
        >
      }
    | null
    | undefined,
): ChatCompactionStatus {
  return getCompactionStatusFromV3Overlay(
    cache?.overlay_objects ?? {},
    cache?.digest?.overlay_object_id ?? null,
  )
}

interface BuildPersistedRunMessagesArgs {
  built: BuiltRunMessages
  runRefById: Map<UUID, V3SyncRunRefLike>
  runRef: V3SyncRunRefLike
  runObject: V3SyncRunObjectLike
}

export function buildPersistedRunMessagesFromV3Sync({
  built: builtRunMessages,
  runRefById,
  runRef,
  runObject,
}: BuildPersistedRunMessagesArgs): ChatMessage[] {
  const parentLastMessageId = getParentLastMessageIdFromV3Sync(
    runRefById,
    runRef.parent_run_id,
  )
  const placement = [
    runRef.run_id,
    runRef.parent_run_id,
    runRef.start_index,
    parentLastMessageId,
  ].join(' ')
  const built = builtRunMessages.get(runObject)
  if (built?.placement === placement) return built.messages

  const lastMessageIndex = runObject.messages.length - 1
  const messages = runObject.messages.map((message, index) => {
    const indexInThread = runRef.start_index + index
    return {
      id: `${runRef.run_id}:${indexInThread}` as RunIdToMsgIndex,
      parent_id:
        index === 0
          ? parentLastMessageId
          : (`${runRef.run_id}:${indexInThread - 1}` as RunIdToMsgIndex),
      run_id: runRef.run_id,
      run_parent_id: runRef.parent_run_id,
      run_user_id: runObject.initialized_by_user_id,
      index_in_thread: indexInThread,
      message,
      deferred_tool_calls:
        index === lastMessageIndex
          ? (runObject.deferred_tool_calls ?? null)
          : null,
    }
  })
  builtRunMessages.set(runObject, { placement, messages })
  return messages
}

export function buildOverlayMessagesFromV3Sync(
  overlayObject: V3SyncOverlayObjectLike<V3SyncOverlayMessageLike>,
): ChatMessage[] {
  return overlayObject.messages.map((message) => ({
    id: message.message_id,
    parent_id: message.parent_id,
    run_id: message.run_id,
    run_parent_id: message.run_parent_id,
    run_user_id: message.run_user_id,
    index_in_thread: message.index_in_thread,
    message: message.message,
    deferred_tool_calls: message.deferred_tool_calls ?? null,
    last_delta_sequence: message.last_sequence ?? null,
  }))
}

function getParentLastMessageIdFromV3Sync(
  runRefById: Map<UUID, V3SyncRunRefLike>,
  parentRunId: UUID | null,
): RunIdToMsgIndex | null {
  if (parentRunId == null) return null
  const parentRunRef = runRefById.get(parentRunId)
  if (!parentRunRef) {
    return null
  }
  return `${parentRunId}:${parentRunRef.start_index + parentRunRef.message_count - 1}` as RunIdToMsgIndex
}

export function buildAllMessagesFromV3Sync<
  RunRefT extends V3SyncRunRefLike,
  OverlayObjectT extends V3SyncOverlayObjectLike<any>,
>(
  manifest: Pick<V3SyncManifestLike<RunRefT>, 'run_refs'>,
  messagesByRunId: Map<UUID, ChatMessage[]>,
  overlayObject: OverlayObjectT | undefined,
): ChatMessage[] {
  const persistedMessages = [...manifest.run_refs]
    .sort(
      (left, right) =>
        new Date(left.created_at).getTime() -
        new Date(right.created_at).getTime(),
    )
    .flatMap((runRef) => messagesByRunId.get(runRef.run_id) ?? [])

  if (!overlayObject) {
    return persistedMessages
  }

  const overlayIsAlreadyRepresented = manifest.run_refs.some(
    (runRef) => runRef.run_id === overlayObject.run_id,
  )
  if (overlayIsAlreadyRepresented) {
    return persistedMessages
  }

  return [
    ...persistedMessages,
    ...(messagesByRunId.get(overlayObject.run_id) ?? []),
  ]
}

export function buildCurrentThreadFromV3Sync<
  RunRefT extends V3SyncRunRefLike,
  OverlayObjectT extends V3SyncOverlayObjectLike<any>,
>(
  manifest: Pick<V3SyncManifestLike<RunRefT>, 'run_refs'>,
  messagesByRunId: Map<UUID, ChatMessage[]>,
  overlayObject: OverlayObjectT | undefined,
  currentRunId: UUID | null,
): ChatMessage[] {
  if (currentRunId == null) return []

  const parentByRun = new Map<UUID, UUID | null>(
    manifest.run_refs.map((runRef) => [runRef.run_id, runRef.parent_run_id]),
  )
  if (overlayObject) {
    parentByRun.set(overlayObject.run_id, overlayObject.parent_run_id)
  }

  const lineage: UUID[] = []
  let node: UUID | null = currentRunId
  while (node) {
    lineage.push(node)
    node = parentByRun.get(node) ?? null
  }
  lineage.reverse()

  return lineage.flatMap((runId) => messagesByRunId.get(runId) ?? [])
}

export function resolveCurrentModelNameFromV3Sync<
  RunRefT extends V3SyncRunRefLike,
  RunObjectT extends Pick<
    V3SyncRunObjectLike,
    'object_id' | 'overridden_model_name'
  >,
  OverlayObjectT extends Pick<V3SyncOverlayObjectLike<any>, 'run_id'>,
>(
  manifest: Pick<V3SyncManifestLike<RunRefT>, 'run_refs'>,
  runObjects: Record<string, RunObjectT>,
  currentRunId: UUID | null,
  overlayObject: OverlayObjectT | undefined,
  persistedCurrentModelName: string | null,
): string | null {
  if (currentRunId == null) {
    return null
  }

  const runRef = manifest.run_refs.find(
    (candidate) => candidate.run_id === currentRunId,
  )
  if (runRef) {
    return runObjects[runRef.object_id]?.overridden_model_name ?? null
  }

  if (overlayObject && overlayObject.run_id === currentRunId) {
    return persistedCurrentModelName
  }

  return persistedCurrentModelName
}
